import { FC, useState } from 'react';
import { Modal } from 'antd';
import apiRequests from 'src/utils/api';
import apiRoutes from 'src/utils/apiRoutes';
import asyncErrorHandler from 'src/utils/asyncErrorHandler';

interface ClientSurveyModalProps {
  open: boolean;
  onClose: () => void;
  onComplete: () => void;
}

const SCORES = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

const getFeedbackLabel = (score: number): string => {
  if (score <= 6) return "What's the main thing we could do better?";
  if (score <= 8) return 'What would make this a 9 or 10 for you?';
  return 'What do you value most about working with us?';
};

const ClientSurveyModal: FC<ClientSurveyModalProps> = ({ open, onClose, onComplete }) => {
  const [step, setStep] = useState<1 | 2>(1);
  const [selectedScore, setSelectedScore] = useState<number | null>(null);
  const [feedback, setFeedback] = useState('');
  const [loading, setLoading] = useState(false);

  const resetState = () => {
    setStep(1);
    setSelectedScore(null);
    setFeedback('');
    setLoading(false);
  };

  const handleSubmit = async () => {
    if (!selectedScore || !feedback.trim()) return;

    try {
      setLoading(true);
      await apiRequests.post(apiRoutes.SURVEY_FEEDBACK, {
        score: selectedScore,
        comment: feedback,
      });
      setStep(2);
    } catch (error) {
      asyncErrorHandler(error);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const handleComplete = () => {
    resetState();
    onComplete();
  };

  const canSubmit = selectedScore !== null && feedback.trim().length > 0;

  return (
    <Modal
      open={open}
      onCancel={step === 2 ? handleComplete : handleClose}
      footer={null}
      centered
      destroyOnClose
      className="[&_.ant-modal-header]:!mb-0"
      title={
        <div className="flex flex-col items-center py-2">
          <span className={`mt-2 text-gray-700 ${step === 1 ? 'text-lg font-semibold' : 'text-2xl font-bold'}`}>
            {step === 1 ? 'Claim my $10': 'Thank you for your feedback.'}
          </span>
        </div>
      }
    >
      {step === 1 ? (
        <div className="flex flex-col gap-5">
          <p className="text-left text-sm text-gray-600">
            How likely are you to recommend Savage Global Marketing to a friend
            or colleague?
          </p>

          <div className="flex items-center gap-1.5">
            {SCORES.map((score) => (
              <button
                key={score}
                onClick={() => setSelectedScore(score)}
                className={`flex flex-1 aspect-square items-center justify-center rounded-full text-sm font-medium transition-all ${
                  selectedScore === score
                    ? 'bg-primary text-white'
                    : 'bg-[#F5F4F7] text-gray-700 hover:bg-gray-200'
                }`}
              >
                {score}
              </button>
            ))}
          </div>

          {selectedScore !== null && (
            <div className="flex flex-col gap-1.5">
              <span className="text-sm text-gray-500">
                {getFeedbackLabel(selectedScore)}
              </span>
              <textarea
                value={feedback}
                placeholder="Type your feedback here"
                onChange={(e) => setFeedback(e.target.value)}
                rows={4}
                autoFocus
                className="w-full resize-none rounded-md border border-gray-200 p-3 outline-none transition-colors focus:border-primary"
              />
            </div>
          )}

          <button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit || loading}
            className="flex w-full items-center justify-center gap-2 rounded-md bg-primary py-3 font-semibold text-white transition-all hover:bg-primary-dark disabled:opacity-60"
          >
            Submit
          </button>
        </div>
      ) : (
        <div className="flex flex-col items-center gap-5">
          <p className="text-center text-lg py-2">
            You will receive a $10 gift card code via email within the next 72 hours.
          </p>

          <button
            type="button"
            onClick={handleComplete}
            className="flex w-full items-center justify-center gap-2 rounded-md bg-primary py-3 font-semibold text-white transition-all hover:bg-primary-dark"
          >
            Close
          </button>
        </div>
      )}
    </Modal>
  );
};

export default ClientSurveyModal;
